import { useEffect, useState } from 'react'
import { styled, Typography, ButtonBase, IconButton } from '@mui/material'
import {
  MicRounded as MicIcon,
  MicOffRounded as MicOffIcon,
} from '@mui/icons-material'
import { UserAvatar } from '$common/avatar'
import {
  useCallStatusText,
  usePvCallOpponentInfo,
} from '$common/react-hooks/call'
import { useRoom } from '$logic/room'
import { formatCallDuration, useCallStorage } from '$logic/call'
import { useOpenedRoomId } from '$logic/opened-room'
import { CallWappPanel } from '$logic/wapp-panels'
import { openCall as openCallPanel, openPvCallManager } from '$logic/router'
import { t } from '$i18n'
import { CALL_BAR_HEIGHT } from '$comp/_app/components/chat/components/conf'
import { AvatarSize } from '$common/avatar/avatar-base/types'
import { dateWithOffset } from '$setup/time-offset'

const Root = styled(ButtonBase)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;

  width: 100%;
  height: ${CALL_BAR_HEIGHT}px;
  padding: 0 16px;
  overflow: hidden;

  &::before {
    content: '';
    display: block;
    position: absolute;
    background: linear-gradient(
      270deg,
      rgba(102, 102, 102, 1) 0%,
      rgba(128, 128, 128, 1) 40%,
      rgba(58, 184, 20, 1) 60%,
      rgba(40, 195, 40, 1) 100%
    );

    right: 0;
    width: 300%;
    height: 100%;
    will-change: transform;
    transition: right 250ms linear;
  }
`

const OpponentInfo = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
  z-index: 1;
`

const DurationTypography = styled(Typography)`
  /* @noflip */
  direction: ltr;
`

const MuteButton = styled(IconButton)`
  transform: translateX(-8px);

  & > svg {
    color: #fff;
  }
`

interface PropTypes {
  isInRoom?: boolean
}
export function PvCallBar({ isInRoom = false }: PropTypes) {
  const [formattedDuration, setFormattedDuration] = useState('')

  const openedRoomId = useOpenedRoomId()
  const { roomId, state, muted } = useCallStorage()

  const { call } = useRoom(roomId) || {}
  const startedAt = call?.startedAt

  useEffect(() => {
    if (!startedAt) return

    const intervalId = setInterval(() => {
      const timeDiff = dateWithOffset() - +new Date(startedAt)
      setFormattedDuration(formatCallDuration(timeDiff))
    }, 1000)

    return () => {
      if (intervalId) clearInterval(intervalId)
    }
  }, [startedAt])

  const { id, name } = usePvCallOpponentInfo()
  const statusText = useCallStatusText()

  const showOpponentInfo = !isInRoom || openedRoomId !== roomId

  if (!id) return null
  return (
    <Root
      disableRipple
      disableTouchRipple
      sx={{
        '&::before': { right: muted && state === 'ongoing' ? '0' : '-200%' },
      }}
      onClick={() => {
        if (state === 'ongoing') openCallPanel()
        else openPvCallManager()
      }}
    >
      {!showOpponentInfo && state === 'outgoing' ? (
        <>
          <Typography variant="subtitle3" color="white" zIndex={1}>
            {statusText}
          </Typography>

          <MuteButton disabled sx={{ opacity: 0.5 }}>
            <MicIcon />
          </MuteButton>
        </>
      ) : (
        <>
          <OpponentInfo>
            {showOpponentInfo && (
              <UserAvatar userId={id} size={AvatarSize._2XS} />
            )}

            <Typography variant="subtitle3" color="white" noWrap>
              {showOpponentInfo ? name : t('call.currentCall')}
            </Typography>

            {state === 'ongoing' && formattedDuration && (
              <>
                <Typography
                  variant="subtitle3"
                  color="white"
                  sx={{ opacity: 0.5 }}
                >
                  /
                </Typography>
                <DurationTypography variant="caption1" color="white">
                  {formattedDuration}
                </DurationTypography>
              </>
            )}
          </OpponentInfo>

          {state === 'ongoing' ? (
            <MuteButton
              onClick={e => {
                e.stopPropagation()
                CallWappPanel.setMuted(!muted)
              }}
            >
              {muted ? <MicOffIcon /> : <MicIcon />}
            </MuteButton>
          ) : (
            <Typography variant="caption1" color="white" zIndex={1}>
              {statusText}
            </Typography>
          )}
        </>
      )}
    </Root>
  )
}
